import { format } from "date-fns";
import { BlockBlobClient } from "@azure/storage-blob";
import { blobToken } from "../api/api-methods";
import { toast } from "react-toastify";
import { eachDayOfInterval } from "date-fns";

export function formatDate(dateString) {
  if (!dateString) return "";
  return format(new Date(dateString), "MMM dd, yyyy");
}
export const uploadFile = async (file, entityType) => {
  const selectedFile = file;

  if (!selectedFile) {
    return;
  }

  const safeFileName = file.name.replace(/\s+/g, "_").replace(/[()]/g, "");

  try {
    const blobData = await blobToken({
      uploadEnitityType: "Images",
      uploadEntityId: safeFileName,
    });

    const sasToken = blobData?.data?.sasToken;
    const containerName = blobData?.data?.containerName;
    const storageAccountName = blobData?.data?.storageAccountName;
    const blobPath = blobData?.data?.blobPath;

    if (!storageAccountName || !containerName || !sasToken) {
      console.error("Missing required blob data:", blobData);
      return;
    }

    const blobUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobPath}?${sasToken}`;

    const blobClient = new BlockBlobClient(blobUrl);

    await blobClient.uploadData(selectedFile).then(() => {});

    return blobUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    // alert("An error occurred during upload. Please try again.");
    return null;
  }
};

export const trimFloatNumber = (number) => {
  return number?.toFixed(0);
};

export const debounce = (fn, delay = 500) => {
  let timer;

  return (...args) => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;

    return payload.exp < currentTime;
  } catch (e) {
    return true; // invalid token => treat as expired
  }
};

export const getRoleBasedRedirect = (role) => {
  switch (role) {
    case "vendor":
      return "/vendor/dashboard";
    case "admin":
      return "/admin/dashboard";
    case "user":
      return "/user/home";
    default:
      return "/";
  }
};

export const getCleanedUrl = (url) => {
  if (!url) return "";
  return url.split("?")[0];
};

export const downloadICSFile = (icsContent, filename = "booking.ics") => {
  const blob = new Blob([icsContent], {
    type: "text/calendar;charset=utf-8",
  });

  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const formatValue = (value, type) => {
  if (value === undefined || value === null) return "-";
  if (type === "currency") {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value}`;
  }

  if (type === "count") {
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return `${value}`;
  }
  return value;
};

export const copyPublicUrl = async (path, successMessage = "URL copied!") => {
  try {
    const url = `${window.location.origin}${path}`;
    await navigator.clipboard.writeText(url);
    successMessage && toast.success(successMessage);
  } catch (err) {
    console.error("Copy failed", err);
  }
};

export const formatTime = (time) => {
  if (!time) return "";
  const [h, m] = time.split(":");
  const date = new Date();
  date.setHours(h, m);
  return format(date, "hh:mm a");
};

export const formatDateRange = (start, end) => {
  if (!start || !end) return "";
  return `${format(new Date(start), "MMM dd")} – ${format(
    new Date(end),
    "MMM dd, yyyy",
  )}`;
};

const WEEKDAY_MAP = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function applyAvailabilityToDateRange({
  startDate,
  endDate,
  openDays, // ["Mon", "Tue", "Wed", ...]
  existingAvailability = {},
}) {
  if (!startDate || !endDate || !openDays?.length) return existingAvailability;

  const openDayIndexes = openDays.map((d) => WEEKDAY_MAP[d]);

  const daysInRange = eachDayOfInterval({
    start: new Date(startDate),
    end: new Date(endDate),
  });

  const updatedAvailability = { ...existingAvailability };

  daysInRange.forEach((day) => {
    const dayIndex = day.getDay(); // 0–6
    if (openDayIndexes.includes(dayIndex)) {
      const key = format(day, "yyyy-MM-dd");
      updatedAvailability[key] = { available: true };
    }
  });

  return updatedAvailability;
}
